'use client';

import { useSyncExternalStore } from 'react';
import { useTheme } from 'next-themes';
import { Sun, Moon } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { setThemeWithFade } from '@/lib/theme-fade';
import { DISPLAY_MODE } from '@/lib/copy';

/**
 * The display-mode toggle, at the bottom of the sidebar, in the form it had before #520
 * (D22). The glyph is the mode you are IN - Moon under NIGHT, Sun under DAY - while the
 * accessible name is the mode a press takes you TO. That split is inherited and deliberate:
 * expanded, the same glyph sits beside "Day mode" and a Switch whose checked state is
 * also the current mode, so the icon has to read as state there; collapsed, the button is
 * named for its action the way every other button is. `theme-toggle.test.tsx` pins both
 * halves so a future reader cannot "correct" one out of step with the other.
 *
 * The words are DAY and NIGHT, from `lib/copy`. T1 named the modes that (D19) and this
 * control, the one place an operator picks between them, was still the last surface saying
 * "bright" and "dark" - two names for one thing on one screen (#505 T8).
 *
 * Two forms, because the rail has two widths. Collapsed (and the air picture freezes it
 * collapsed) it is the icon alone, with the action in both the accessible name and the
 * tooltip - a lucide icon with no child is `aria-hidden`, so without that label the button has
 * no name at all. Expanded it is the labelled "Day mode" row with a Switch, where there is
 * room for the words.
 *
 * The change cross-fades the colour tokens through `setThemeWithFade`, and the choice
 * persists under `shovron-theme` through next-themes, which also applies it before first
 * paint on the next load. It changes the CHROME only: D3 had the basemap follow the mode
 * too, and swapping the ground out from under a picture the operator had already set was
 * reported as a bug. The basemap is its own decision, in the Layers menu.
 */
const subscribeNever = () => () => {};

export function ThemeToggle({ collapsed }: { collapsed: boolean }) {
  const { resolvedTheme, setTheme } = useTheme();
  // next-themes resolves after mount. Until then the control renders in its NIGHT form and
  // disabled, so the server render and the first client render agree. A store subscription
  // rather than a set-state effect: the server snapshot is false, the client snapshot is
  // true, and nothing re-renders in an effect.
  const mounted = useSyncExternalStore(subscribeNever, () => true, () => false);

  const isLight = mounted && resolvedTheme === 'light';
  const label = isLight ? DISPLAY_MODE.switchToNight : DISPLAY_MODE.switchToDay;
  const glyph = isLight ? (
    <Sun className="size-4" strokeWidth={1.5} />
  ) : (
    <Moon className="size-4" strokeWidth={1.5} />
  );

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setThemeWithFade(setTheme, isLight ? 'dark' : 'light')}
        disabled={!mounted}
        title={label}
        aria-label={label}
        className="flex items-center justify-center w-full py-2 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors disabled:opacity-50"
      >
        {glyph}
      </button>
    );
  }

  return (
    <div className="mt-3 flex items-center justify-between px-3 py-2 text-sm font-medium text-sidebar-foreground/60">
      <span className="flex items-center gap-2">
        {glyph}
        {DISPLAY_MODE.dayRow}
      </span>
      <Switch
        checked={isLight}
        onCheckedChange={(checked) => setThemeWithFade(setTheme, checked ? 'light' : 'dark')}
        disabled={!mounted}
        aria-label={DISPLAY_MODE.dayToggle}
      />
    </div>
  );
}
